import {
  GAOffersCompareClick,
  GAOffersCompareModelsClick,
  GAOffersCompareNowClick,
  GAOffersPageLoad,
  GAOffersShowMoreClick,
  GAOffersSpecDifferencesClick,
} from '@analytics/classes/ga.offers.class';
import { AnalyticsService } from '@analytics/services/analytics.service';
import { Injectable } from '@angular/core';
import { CompleteOrderService } from '@complete-order/services/complete-order.service';
import { OfferStatus } from '@dgx-pr-spa/shared/util';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ViewOffersService } from '@view-offers/services/view-offers.service';
import { filter, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import * as fromGA from '../actions';

@Injectable()
export class GAOffersEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly analyticsService: AnalyticsService,
    private readonly viewOffersService: ViewOffersService,
    private readonly completeOrderService: CompleteOrderService
  ) {}

  PageLoad$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromGA.OfferPageLoad),
        switchMap(() => this.viewOffersService.offersLoaded$),
        filter(Boolean),
        withLatestFrom(
          this.viewOffersService.offers$,
          this.completeOrderService.createOrderLoaded$
        ),
        filter(
          ([, offers, orderCreated]) =>
            !orderCreated /* ignore offers event after order is complete */ &&
            offers?.offer?.offer_status !==
              OfferStatus.TAKEN /* ignore offers event if offer already taken */ &&
            offers?.replacement_offers?.length >
              1 /* ignore offers event if only 1 replacement as auto redirects to product details page */
        ),
        tap(([, offers]) => {
          this.analyticsService.eventEmitter(
            new GAOffersPageLoad(offers).gaData
          );
        })
      ),
    { dispatch: false }
  );

  ShowMoreClick$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromGA.OfferShowMoreClick),
        map((action) => action.payload),
        withLatestFrom(this.viewOffersService.offers$),
        tap(([offerType, offers]) => {
          const gaData = new GAOffersShowMoreClick(offers, offerType).gaData;
          this.analyticsService.eventEmitter(gaData);
        })
      ),
    { dispatch: false }
  );

  ComparisonClick$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromGA.OfferComparisonClick),
        map((action) => action.payload),
        withLatestFrom(
          this.viewOffersService.offers$,
          this.viewOffersService.comparisons$
        ),
        tap(([comparison, offers, comparisons]) => {
          const selected = this.viewOffersService.filterOfferSelected(
            comparison,
            comparisons
          );
          if (selected) {
            const gaData = new GAOffersCompareClick(comparison, offers).gaData;
            this.analyticsService.eventEmitter(gaData);
          }
        })
      ),
    { dispatch: false }
  );

  CompareModelsClick$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromGA.OfferCompareModelsClick),
        withLatestFrom(this.viewOffersService.offers$),
        tap(([, offers]) => {
          const gaData = new GAOffersCompareModelsClick(offers).gaData;
          this.analyticsService.eventEmitter(gaData);
        })
      ),
    { dispatch: false }
  );

  CompareNowClick$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromGA.OfferCompareNowClick),
        withLatestFrom(
          this.viewOffersService.offers$,
          this.viewOffersService.comparisons$
        ),
        tap(([, offers, comparisons]) => {
          const gaData = new GAOffersCompareNowClick(offers, comparisons)
            .gaData;
          this.analyticsService.eventEmitter(gaData);
        })
      ),
    { dispatch: false }
  );

  ToggleSpecDifferencesClick$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromGA.OfferSpecDifferencesClick),
        withLatestFrom(
          this.viewOffersService.offers$,
          this.viewOffersService.comparisons$
        ),
        tap(([, offers, comparisons]) => {
          const gaData = new GAOffersSpecDifferencesClick(offers, comparisons)
            .gaData;
          this.analyticsService.eventEmitter(gaData);
        })
      ),
    { dispatch: false }
  );
}
